<script setup>
/**
 * Which of the business's places a Tell sends as a pin, and the panel that adds, edits or removes
 * one (spec §4 P-4.2). There is no page of places: they are made where they are used.
 *
 * INLINE, NOT A DIALOG. Nothing in the composer teleports (`specs/task-compose-screens.spec.js`,
 * rule 5), and `Dialog` does.
 *
 * The point is read HERE by the sidecar's own rules (`helpers/map-point.js`) and shown back as
 * numbers before anything is saved; the server reads it again from the same `where`.
 */
import { computed, onMounted, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { WASLO_ERROR_CODES, wasloApi } from '../../api/client.js';
import { parseMapPoint } from '../../helpers/map-point.js';

const props = defineProps({
  /** The chosen place's id; `0` while none is chosen. */
  modelValue: { type: Number, default: 0 },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();
const key = name => `WASLO.TASKS.COMPOSE.PLACES.${name}`;

const places = ref([]);
const loadError = ref('');
/** `null`, or the place being written: `{ id: number|null, nameEn, nameAr, addressEn, addressAr, where }`. */
const editing = ref(null);
const panelError = ref('');
const busy = ref(false);

const load = async () => {
  try {
    places.value = await wasloApi.listPlaces(accountId.value);
    loadError.value = '';
  } catch {
    loadError.value = t(key('LOAD_FAILED'));
  }
};
onMounted(load);

const nameOf = place => place.nameEn || place.nameAr;
const options = computed(() =>
  places.value.map(place => ({ value: String(place.id), label: nameOf(place) }))
);
const chosen = computed(
  () => places.value.find(place => place.id === props.modelValue) ?? null
);

const point = computed(() =>
  editing.value === null || editing.value.where.trim() === ''
    ? null
    : parseMapPoint(editing.value.where)
);
const pointMessage = computed(() => {
  if (point.value === null) return '';
  if (point.value.ok) {
    // Strings, not numbers: `t` writes numbers through the formatter, which rounds to three
    // places, and a pin needs every digit the owner pasted.
    return t(key('POINT'), {
      lat: String(point.value.point.latitude),
      lng: String(point.value.point.longitude),
    });
  }
  return t(key(`WHERE_${point.value.reason.toUpperCase()}`));
});

const blank = () => ({
  id: null,
  nameEn: '',
  nameAr: '',
  addressEn: '',
  addressAr: '',
  where: '',
});
const startAdd = () => {
  editing.value = blank();
  panelError.value = '';
};
const startEdit = () => {
  const place = chosen.value;
  if (place === null) return;
  editing.value = {
    id: place.id,
    nameEn: place.nameEn,
    nameAr: place.nameAr,
    addressEn: place.addressEn,
    addressAr: place.addressAr,
    where: `${place.latitude}, ${place.longitude}`,
  };
  panelError.value = '';
};
const set = (field, value) => {
  editing.value = { ...editing.value, [field]: value };
};

const save = async () => {
  if (
    editing.value.nameEn.trim() === '' &&
    editing.value.nameAr.trim() === ''
  ) {
    panelError.value = t(key('NAME_MISSING'));
    return;
  }
  if (!point.value?.ok) {
    panelError.value = t(key('WHERE_NO_POINT'));
    return;
  }
  busy.value = true;
  try {
    const { id, ...body } = editing.value;
    const saved =
      id === null
        ? await wasloApi.addPlace(accountId.value, body)
        : await wasloApi.updatePlace(accountId.value, id, body);
    await load();
    editing.value = null;
    emit('update:modelValue', saved.id);
  } catch (error) {
    panelError.value =
      error?.status === 409 ? t(key('LIMIT')) : t(key('SAVE_FAILED'));
  } finally {
    busy.value = false;
  }
};

const remove = async () => {
  busy.value = true;
  try {
    await wasloApi.removePlace(accountId.value, editing.value.id);
    await load();
    editing.value = null;
    emit('update:modelValue', 0);
  } catch (error) {
    panelError.value =
      error?.code === WASLO_ERROR_CODES.PLACE_IN_USE
        ? t(key('IN_USE'), {
            tasks: (error.tasks ?? []).join(t(key('LIST_JOIN'))),
          })
        : t(key('SAVE_FAILED'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-place-picker>
    <WasloSelect
      :model-value="modelValue > 0 ? String(modelValue) : ''"
      :options="options"
      :label="t(key('PICK'))"
      :placeholder="t(key('PICK_PLACEHOLDER'))"
      :message="loadError"
      :message-type="loadError ? 'error' : 'info'"
      data-waslo-place-select
      @update:model-value="value => emit('update:modelValue', Number(value))"
    />
    <div class="flex flex-wrap gap-2">
      <Button
        :label="t(key('ADD'))"
        icon="i-lucide-map-pin-plus"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-place-add
        @click="startAdd"
      />
      <Button
        v-if="chosen"
        :label="t(key('EDIT'))"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-place-edit
        @click="startEdit"
      />
    </div>

    <div
      v-if="editing"
      class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
      data-waslo-place-panel
    >
      <div class="grid gap-3 sm:grid-cols-2">
        <WasloInput
          :model-value="editing.nameEn"
          :label="t(key('NAME_EN'))"
          :max-length="100"
          data-waslo-place-name-en
          @update:model-value="value => set('nameEn', value)"
        />
        <WasloInput
          :model-value="editing.nameAr"
          :label="t(key('NAME_AR'))"
          :max-length="100"
          data-waslo-place-name-ar
          @update:model-value="value => set('nameAr', value)"
        />
        <WasloInput
          :model-value="editing.addressEn"
          :label="t(key('ADDRESS_EN'))"
          :max-length="300"
          data-waslo-place-address-en
          @update:model-value="value => set('addressEn', value)"
        />
        <WasloInput
          :model-value="editing.addressAr"
          :label="t(key('ADDRESS_AR'))"
          :max-length="300"
          data-waslo-place-address-ar
          @update:model-value="value => set('addressAr', value)"
        />
      </div>
      <WasloInput
        :model-value="editing.where"
        :label="t(key('WHERE'))"
        :hint="t(key('WHERE_HINT'))"
        :max-length="2000"
        :message="pointMessage"
        :message-type="point && !point.ok ? 'error' : 'info'"
        data-waslo-place-where
        @update:model-value="value => set('where', value)"
      />
      <p
        v-if="panelError"
        class="mb-0 text-sm text-n-ruby-11"
        data-waslo-place-error
      >
        {{ panelError }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          :label="t(key('SAVE'))"
          size="sm"
          :is-loading="busy"
          data-waslo-place-save
          @click="save"
        />
        <Button
          :label="t(key('CANCEL'))"
          variant="faded"
          color="slate"
          size="sm"
          @click="editing = null"
        />
        <Button
          v-if="editing.id !== null"
          :label="t(key('REMOVE'))"
          variant="faded"
          color="ruby"
          size="sm"
          data-waslo-place-remove
          @click="remove"
        />
      </div>
    </div>
  </div>
</template>
